import React, { useState } from 'react';
import { X, AlertTriangle, Info } from 'lucide-react';
import { Button } from '../common/Button';
import Modal from '../common/Modal';
import { CANCELLATION_REASONS, CancellationReasonType, CancelSubscriptionRequest } from '../../services/subscriptionService';

interface CancellationReasonModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (request: CancelSubscriptionRequest) => void;
  isLoading?: boolean;
  planName?: string;
  endDate?: string;
}

const REASON_LABELS: Record<CancellationReasonType, string> = {
  [CANCELLATION_REASONS.TOO_EXPENSIVE]: 'Too expensive',
  [CANCELLATION_REASONS.NOT_USING_ENOUGH]: 'Not using it enough',
  [CANCELLATION_REASONS.POOR_QUALITY]: 'Poor quality or experience',
  [CANCELLATION_REASONS.MISSING_FEATURES]: 'Missing features I need',
  [CANCELLATION_REASONS.FOUND_ALTERNATIVE]: 'Found a better alternative',
  [CANCELLATION_REASONS.TECHNICAL_ISSUES]: 'Technical issues or bugs',
  [CANCELLATION_REASONS.TEMPORARY_PAUSE]: 'Taking a temporary break',
  [CANCELLATION_REASONS.POOR_SUPPORT]: 'Poor support or no help from the team',
  [CANCELLATION_REASONS.OTHER]: 'Other (please specify)',
};

export const CancellationReasonModal: React.FC<CancellationReasonModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
  planName = 'your subscription',
  endDate
}) => {
  const [selectedReasons, setSelectedReasons] = useState<CancellationReasonType[]>([]);
  const [otherReason, setOtherReason] = useState('');
  const [feedback, setFeedback] = useState('');
  const [errors, setErrors] = useState<{ reasons?: string; otherReason?: string }>({});

  const handleReasonToggle = (reason: CancellationReasonType) => {
    setSelectedReasons(prev => {
      const newReasons = prev.includes(reason)
        ? prev.filter(r => r !== reason)
        : [...prev, reason];
      
      // Clear errors when user makes a selection
      if (newReasons.length > 0) {
        setErrors(prev => ({ ...prev, reasons: undefined }));
      }
      
      return newReasons;
    });

    // Clear other reason text if "Other" is deselected
    if (reason === CANCELLATION_REASONS.OTHER && selectedReasons.includes(reason)) {
      setOtherReason('');
      setErrors(prev => ({ ...prev, otherReason: undefined }));
    }
  };

  const handleSubmit = () => {
    const newErrors: { reasons?: string; otherReason?: string } = {};

    // Validate that at least one reason is selected
    if (selectedReasons.length === 0) {
      newErrors.reasons = 'Please select at least one reason for cancellation';
    }

    // Validate that if "Other" is selected, a reason is provided
    if (selectedReasons.includes(CANCELLATION_REASONS.OTHER) && !otherReason.trim()) {
      newErrors.otherReason = 'Please provide a reason when selecting "Other"';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Build the request object
    const request: CancelSubscriptionRequest = {
      reasons: selectedReasons,
      otherReason: selectedReasons.includes(CANCELLATION_REASONS.OTHER) ? otherReason.trim() : undefined,
      feedbackOptional: feedback.trim() || undefined,
    };

    onConfirm(request);
  };

  const handleClose = () => {
    if (!isLoading) {
      // Reset form state
      setSelectedReasons([]);
      setOtherReason('');
      setFeedback('');
      setErrors({});
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Tell us why you're cancelling"
      maxWidth="600px"
      hideCloseButton={true}
      preventOutsideClick={true}
    >
      <div className="p-6 relative overflow-hidden">
        {/* Warning message - Following dashboard card design pattern */}
        <div className="mb-6 p-4 rounded-xl border border-amber-500/20 dark:border-amber-400/20 bg-amber-500/5 dark:bg-amber-400/5 backdrop-blur-sm">
          <div className="flex items-center">
            <div className="p-2 rounded-full bg-amber-500/10 dark:bg-amber-400/10 mr-4">
              <Info className="w-6 h-6 text-amber-500 dark:text-amber-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-base font-semibold text-text-primary-light dark:text-text-primary-dark">
                Before you cancel {planName}
              </h3>
              <p className="mt-1 text-text-secondary-light dark:text-text-secondary-dark text-sm">
                You'll still have access until <span className="font-medium text-text-primary-light dark:text-text-primary-dark">{endDate}</span>. Your feedback helps us improve MockTrail for everyone.
              </p>
            </div>
          </div>
        </div>

        {/* Reason selection - Following FeedbackModal design pattern */}
        <div className="mb-6">
          <label className="block text-lg font-semibold font-heading text-text-primary-light dark:text-text-primary-dark mb-4">
            What's the main reason for cancelling?
          </label>
          
          <div className="space-y-3">
            {Object.entries(REASON_LABELS).map(([reason, label]) => {
              const isSelected = selectedReasons.includes(reason as CancellationReasonType);
              return (
                <label
                  key={reason}
                  className={`flex items-start p-3 rounded-lg border cursor-pointer transition-all duration-200 hover:border-primary-light/50 dark:hover:border-primary-dark/50 ${
                    isSelected
                      ? 'border-primary-light dark:border-primary-dark bg-primary-light/5 dark:bg-primary-dark/5'
                      : 'border-border-light dark:border-border-dark'
                  }`}
                >
                  <div className="relative flex items-center">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleReasonToggle(reason as CancellationReasonType)}
                      className="sr-only"
                      disabled={isLoading}
                    />
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200 ${
                      isSelected
                        ? 'border-primary-light dark:border-primary-dark bg-primary-light dark:bg-primary-dark'
                        : 'border-border-light dark:border-border-dark bg-background-light dark:bg-background-dark'
                    }`}>
                      {isSelected && (
                        <svg className="w-3 h-3 text-white dark:text-black" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </div>
                  <span className="ml-3 text-sm text-text-primary-light dark:text-text-primary-dark font-medium">
                    {label}
                  </span>
                </label>
              );
            })}
          </div>
          
          {errors.reasons && (
            <p className="mt-2 text-sm text-red-500 dark:text-red-400">{errors.reasons}</p>
          )}
        </div>

        {/* Other reason text input - Following FeedbackModal design pattern */}
        {selectedReasons.includes(CANCELLATION_REASONS.OTHER) && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-text-primary-light dark:text-text-primary-dark mb-2">
              Please specify your reason *
            </label>
            <div className="relative rounded-lg overflow-hidden group bg-background-light/50 dark:bg-background-dark/50 border border-border-light dark:border-border-dark focus-within:border-primary-light dark:focus-within:border-primary-dark focus-within:ring-2 focus-within:ring-primary-light/20 dark:focus-within:ring-primary-dark/20 transition-all duration-200">
              <textarea
                value={otherReason}
                onChange={(e) => {
                  setOtherReason(e.target.value);
                  if (e.target.value.trim()) {
                    setErrors(prev => ({ ...prev, otherReason: undefined }));
                  }
                }}
                placeholder="Tell us more about your reason for cancelling..."
                rows={3}
                maxLength={1000}
                disabled={isLoading}
                className="w-full p-3 text-base bg-transparent border-0 resize-none focus:outline-none text-text-primary-light dark:text-text-primary-dark placeholder-text-secondary-light dark:placeholder-text-secondary-dark"
              />
            </div>
            <div className="flex justify-between mt-1">
              {errors.otherReason && (
                <p className="text-sm text-red-500 dark:text-red-400">{errors.otherReason}</p>
              )}
              <span className="text-xs text-text-secondary-light dark:text-text-secondary-dark ml-auto">
                {otherReason.length}/1000
              </span>
            </div>
          </div>
        )}

        {/* Optional feedback - Following FeedbackModal design pattern */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-text-primary-light dark:text-text-primary-dark mb-2">
            Any additional feedback? (Optional)
          </label>
          <div className="relative rounded-lg overflow-hidden group bg-background-light/50 dark:bg-background-dark/50 border border-border-light dark:border-border-dark focus-within:border-primary-light dark:focus-within:border-primary-dark focus-within:ring-2 focus-within:ring-primary-light/20 dark:focus-within:ring-primary-dark/20 transition-all duration-200">
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Share any suggestions or comments to help us improve..."
              rows={3}
              maxLength={500}
              disabled={isLoading}
              className="w-full p-3 text-sm bg-transparent border-0 resize-none focus:outline-none text-text-primary-light dark:text-text-primary-dark placeholder-text-secondary-light dark:placeholder-text-secondary-dark"
            />
          </div>
          <span className="text-xs text-text-secondary-light dark:text-text-secondary-dark mt-1 block">
            {feedback.length}/500
          </span>
        </div>

        {/* Action buttons - Following FeedbackModal design pattern */}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          <Button
            variant="outline"
            onClick={handleClose}
            className="w-full sm:w-auto"
            disabled={isLoading}
          >
            Keep Subscription
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            className="w-full sm:w-auto !bg-red-500 hover:!bg-red-600 !border-red-500 !shadow-red-500/30 relative overflow-hidden group"
            loading={isLoading}
            disabled={selectedReasons.length === 0}
          >
            <span className="relative z-10">
              {isLoading ? 'Cancelling...' : 'Confirm Cancellation'}
            </span>
          </Button>
        </div>
      </div>
    </Modal>
  );
}; 